import PropTypes from 'prop-types';
import { globalizeSelectors } from '../Utils';
import { COLDATACLASS_SPHERE } from 'js/Constants';
import type {
  FormulaCanInput,
  FormulaChange,
  FormulaVersion,
  LocalFormula,
  MatchFormula,
  MatchingResponse,
  Nullable,
  PrimerColour,
  ProductWithFormula,
  Triplet,
} from 'js/api/Formula';
import type { Refl_Type } from 'innovatint_base/common/color_calc/color_conv';
import type {
  ClosestSearchColorCard,
  ColorSearchResponsePayload,
  CompatibleLocalFormulaState,
  CustomFormulaMode,
  FetchColorDetailsPayload,
  FetchCompatibleLocalFormulaPayload,
  FormulaAction,
  FormulaColorDetails,
  FormulaCorrectionResult,
  FormulaState,
  LoadFormulaPayload,
  MatchFormulaPayload,
  PrimerColourState,
  SearchClosestPayload,
  SynthesizePropertyPayload,
} from '../types/Formula';
import type { OrderFormula } from '../types/Order';

const types = {
  CLEAR_SEARCHES: 'FORMULA/CLEAR_SEARCHES',

  FETCH_COLORS: 'FETCH_COLORS',
  FETCH_FORMULA_REQUEST_COLORS: 'FETCH_FORMULA_REQUEST_COLORS',
  FETCH_COLORS_PENDING: 'FETCH_COLORS_PENDING',
  FETCH_COLORS_FULFILLED: 'FETCH_COLORS_FULFILLED',
  FETCH_COLORS_REJECTED: 'FETCH_COLORS_REJECTED',
  FETCH_PRODUCT_CARDS: 'FORMULA/FETCH_PRODUCT_CARDS',
  FETCH_PRODUCT_CARDS_PENDING: 'FORMULA/FETCH_PRODUCT_CARDS_PENDING',
  FETCH_PRODUCT_CARDS_FULFILLED: 'FORMULA/FETCH_PRODUCT_CARDS_FULFILLED',
  FETCH_PRODUCT_CARDS_REJECTED: 'FORMULA/FETCH_PRODUCT_CARDS_REJECTED',

  FETCH_PROD_WITH_FORMULA: 'FORMULA/PROD_WITH_FORMULA',
  FETCH_PROD_WITH_FORMULA_PENDING: 'FORMULA/PROD_WITH_FORMULA_PENDING',
  FETCH_PROD_WITH_FORMULA_FULFILLED: 'FORMULA/PROD_WITH_FORMULA_FULFILLED',
  FETCH_PROD_WITH_FORMULA_REJECTED: 'FORMULA/PROD_WITH_FORMULA_REJECTED',

  SET_COLOR_DETAILS: 'FORMULA/SET_COLOR_DETAILS',
  SET_CLOSEST_SEARCH_COLORCARD: 'FORMULA/SET_CLOSEST_SEARCH_COLORCARD',

  FETCH_FORMULA: 'FETCH_FORMULA',
  FETCH_FORMULA_PENDING: 'FETCH_FORMULA_PENDING',
  FETCH_FORMULA_FULFILLED: 'FETCH_FORMULA_FULFILLED',
  FETCH_FORMULA_REJECTED: 'FETCH_FORMULA_REJECTED',

  FETCH_LOCAL_FORMULA: 'FORMULA/FETCH_LOCAL_FORMULA',
  FETCH_LOCAL_FORMULA_PENDING: 'FORMULA/FETCH_LOCAL_FORMULA_PENDING',
  FETCH_LOCAL_FORMULA_FULFILLED: 'FORMULA/FETCH_LOCAL_FORMULA_FULFILLED',
  FETCH_LOCAL_FORMULA_REJECTED: 'FORMULA/FETCH_LOCAL_FORMULA_REJECTED',
  HIDE_LOCAL_FORMULA: 'FORMULA/HIDE_LOCAL_FORMULA',

  SET_CUSTOM_FORMULA_MODE: 'FORMULA/SET_CUSTOM_FORMULA_MODE',

  FETCH_COMPATIBLE_LOCALFRM_PROD: 'FORMULA/FETCH_COMPATIBLE_LOCALFRM_PROD',
  FETCH_COMPATIBLE_LOCALFRM_PROD_PENDING:
    'FORMULA/FETCH_COMPATIBLE_LOCALFRM_PROD_PENDING',
  FETCH_COMPATIBLE_LOCALFRM_PROD_FULFILLED:
    'FORMULA/FETCH_COMPATIBLE_LOCALFRM_PROD_FULFILLED',
  FETCH_COMPATIBLE_LOCALFRM_PROD_REJECTED:
    'FORMULA/FETCH_COMPATIBLE_LOCALFRM_PROD_REJECTED',

  SYNTHESIZE_RGB: 'FORMULA/SYNTHESIZE_RGB',
  SYNTHESIZE_RGB_BEFORE_EDIT: 'FORMULA/SYNTHESIZE_RGB_BEFORE_EDIT',
  SYNTHESIZE_TSR: 'FORMULA/SYNTHESIZE_TSR',

  SEARCH_CLOSEST: 'FORMULA/SEARCH_CLOSEST',
  SEARCH_CLOSEST_PENDING: 'FORMULA/SEARCH_CLOSEST_PENDING',
  SEARCH_CLOSEST_FULFILLED: 'FORMULA/SEARCH_CLOSEST_FULFILLED',
  SEARCH_CLOSEST_REJECTED: 'FORMULA/SEARCH_CLOSEST_REJECTED',

  MATCH_FORMULA: 'FORMULA/MATCH_FORMULA',
  MATCH_FORMULA_PENDING: 'FORMULA/MATCH_FORMULA_PENDING',
  MATCH_FORMULA_FULFILLED: 'FORMULA/MATCH_FORMULA_FULFILLED',
  MATCH_FORMULA_REJECTED: 'FORMULA/MATCH_FORMULA_REJECTED',
  ALTERNATIVE_MATCHING: 'FORMULA/ALTERNATIVE_MATCHING',

  CORRECT_FORMULA: 'FORMULA/CORRECT_FORMULA',
  CORRECT_FORMULA_PENDING: 'FORMULA/CORRECT_FORMULA_PENDING',
  CORRECT_FORMULA_FULFILLED: 'FORMULA/CORRECT_FORMULA_FULFILLED',
  CORRECT_FORMULA_REJECTED: 'FORMULA/CORRECT_FORMULA_REJECTED',

  FETCH_COLOR_DETAILS: 'FORMULA/FETCH_COLOR_DETAILS',
  FETCH_COLOR_DETAILS_PENDING: 'FORMULA/FETCH_COLOR_DETAILS_PENDING',
  FETCH_COLOR_DETAILS_FULFILLED: 'FORMULA/FETCH_COLOR_DETAILS_FULFILLED',
  FETCH_COLOR_DETAILS_REJECTED: 'FORMULA/FETCH_COLOR_DETAILS_REJECTED',

  CLEAR_PRIMER: 'FORMULA/CLEAR_PRIMER',
  CHOOSE_PRIMER: 'FORMULA/CHOOSE_PRIMER',
  CHOOSE_PRIMER_PENDING: 'FORMULA/CHOOSE_PRIMER_PENDING',
  CHOOSE_PRIMER_FULFILLED: 'FORMULA/CHOOSE_PRIMER_FULFILLED',
  CHOOSE_PRIMER_REJECTED: 'FORMULA/CHOOSE_PRIMER_REJECTED',
  ACK_PRIMER: 'FORMULA/ACK_PRIMER',

  FETCH_FORMULA_CHANGES: 'FORMULA/FETCH_FORMULA_CHANGES',
  FETCH_FORMULA_CHANGES_PENDING: 'FORMULA/FETCH_FORMULA_CHANGES_PENDING',
  FETCH_FORMULA_CHANGES_FULFILLED: 'FORMULA/FETCH_FORMULA_CHANGES_FULFILLED',
  FETCH_FORMULA_CHANGES_REJECTED: 'FORMULA/FETCH_FORMULA_CHANGES_REJECTED',
} as const;

export const CUSTOM_FORMULA_ORIGINAL: CustomFormulaMode = 'ORIGINAL';
export const CUSTOM_FORMULA_COMPATIBLE: CustomFormulaMode = 'COMPATIBLE';
export const CUSTOM_FORMULA_NEW: CustomFormulaMode = 'NEW';

const actions = {
  /**
   * Reading autohorisation token from local storage
   * @param search {string} to search
   * @returns action
   */
  clearSearches: (): FormulaAction => ({ type: types.CLEAR_SEARCHES }),

  fetchProductsWithFormulas: (
    colourid?: Nullable<number>
  ): FormulaAction<Nullable<number> | undefined> => ({
    type: types.FETCH_PROD_WITH_FORMULA,
    payload: colourid,
  }),
  fetchLocalFormulaCompatibleProducts: (
    itemid: Nullable<number>,
    baseid: number
  ): FormulaAction<FetchCompatibleLocalFormulaPayload> => ({
    type: types.FETCH_COMPATIBLE_LOCALFRM_PROD,
    payload: { itemid, baseid },
  }),
  fetchCardsForProduct: (
    productid?: Nullable<number>
  ): FormulaAction<Nullable<number> | undefined> => ({
    type: types.FETCH_PRODUCT_CARDS,
    payload: productid,
  }),
  searchColors: (
    search: Nullable<string>
  ): FormulaAction<Nullable<string>> => ({
    type: types.FETCH_COLORS,
    payload: search,
  }),
  searchColorsForFormulaRequest: (
    search: Nullable<string>
  ): FormulaAction<Nullable<string>> => ({
    type: types.FETCH_FORMULA_REQUEST_COLORS,
    payload: search,
  }),

  loadFormula: (
    colourid: number,
    productid: number,
    can: Nullable<FormulaCanInput> = null,
    expectedBaseid: Nullable<number> = null
  ): FormulaAction<LoadFormulaPayload> => ({
    type: types.FETCH_FORMULA,
    payload: { colourid, productid, can, expectedBaseid },
  }),

  // Loading local formulas from database
  searchLocalFormulas: (
    search: Nullable<string> | undefined
  ): FormulaAction<Nullable<string> | undefined> => ({
    type: types.FETCH_LOCAL_FORMULA,
    payload: search,
  }),
  // "Delete" -actually hide- local formula
  hideLocalFormula: (itemid: number): FormulaAction<number> => ({
    type: types.HIDE_LOCAL_FORMULA,
    payload: itemid,
  }),
  // Set custom formula mode
  // mode can be one of CUSTOM_FORMULA_ORIGINAL, CUSTOM_FORMULA_COMPATIBLE, CUSTOM_FORMULA_NEW
  setCustomFormulaMode: (
    mode: CustomFormulaMode
  ): FormulaAction<CustomFormulaMode> => ({
    type: types.SET_CUSTOM_FORMULA_MODE,
    payload: mode,
  }),

  synthesizeRgb: (
    formula: Nullable<OrderFormula>
  ): FormulaAction<SynthesizePropertyPayload> => ({
    type: types.SYNTHESIZE_RGB,
    payload: { before: false, formula, property: 'rgb' },
  }),
  synthesizeRgbBeforeEdit: (
    formula: Nullable<OrderFormula>
  ): FormulaAction<SynthesizePropertyPayload> => ({
    type: types.SYNTHESIZE_RGB_BEFORE_EDIT,
    payload: { before: true, formula, property: 'rgb' },
  }),
  synthesizeTsr: (
    formula: Nullable<OrderFormula>
  ): FormulaAction<SynthesizePropertyPayload> => ({
    type: types.SYNTHESIZE_TSR,
    payload: { before: false, formula, property: 'tsr' },
  }),

  searchClosest: (
    lab: Triplet,
    productid: number,
    can: FormulaCanInput,
    colourid: Nullable<number> = null,
    coldataclass: Nullable<string> = COLDATACLASS_SPHERE,
    refl: Nullable<Refl_Type> = null,
    colourcardid: Nullable<number> = null
  ): FormulaAction<SearchClosestPayload> => ({
    type: types.SEARCH_CLOSEST,
    payload: {
      lab,
      productid,
      can,
      colourid,
      coldataclass,
      refl,
      colourcardid,
    },
  }),
  matchFormula: (
    refl: Refl_Type,
    productid: number,
    can: FormulaCanInput,
    baseids: Nullable<number[]> = null,
    template: Nullable<string> = null,
    cntids: Nullable<number[]> = null
  ): FormulaAction<MatchFormulaPayload> => ({
    type: types.MATCH_FORMULA,
    payload: { refl, productid, can, baseids, template, cntids },
  }),
  correctFormula: (): FormulaAction => ({ type: types.CORRECT_FORMULA }),

  fetchColorDetails: (
    colourid: number
  ): FormulaAction<FetchColorDetailsPayload> => ({
    type: types.FETCH_COLOR_DETAILS,
    payload: { colourid },
  }),
  setColorDetails: (
    color: FormulaColorDetails
  ): FormulaAction<FormulaColorDetails> => ({
    type: types.SET_COLOR_DETAILS,
    payload: color,
  }),
  alternativeMatching: (value: boolean): FormulaAction<boolean> => ({
    type: types.ALTERNATIVE_MATCHING,
    payload: value,
  }),

  setClosestSearchColorCard: (
    colorCard: ClosestSearchColorCard
  ): FormulaAction<ClosestSearchColorCard> => ({
    type: types.SET_CLOSEST_SEARCH_COLORCARD,
    payload: colorCard,
  }),

  // Initiates the primer recommendation query if enabled
  choosePrimer: (): FormulaAction => ({ type: types.CHOOSE_PRIMER }),

  clearPrimer: (): FormulaAction => ({
    type: types.CLEAR_PRIMER,
  }),

  // Dismisses the primer recommendation card
  acknowledgePrimer: (): FormulaAction => ({
    type: types.ACK_PRIMER,
  }),

  // fetches formula changes
  fetchFormulaChanges: (): FormulaAction => ({
    type: types.FETCH_FORMULA_CHANGES,
  }),
};

/*
 * prop type for state shape
 */
const propType = PropTypes.shape({
  match_rs: PropTypes.object,
  correct_rs: PropTypes.object,
  match_rs_error: PropTypes.object,
  correct_rs_error: PropTypes.object,
  match_rs_start: PropTypes.bool,
  correct_rs_start: PropTypes.bool,
  search_closest_rs: PropTypes.object,
  search_closest_rs_start: PropTypes.bool,
  cntinformula: PropTypes.array,
  color_details: PropTypes.shape({
    reflectance: PropTypes.object,
    lab: PropTypes.array,
  }),
  rules: PropTypes.shape({
    cnt_violations: PropTypes.array,
  }),
  closest_search_colorcard: PropTypes.object,
});

const initial_state: FormulaState = {
  // Order details
  colors: [],
  color_search_str: null,
  fetch_colors_error: null,
  fetch_colors_start: false,

  product_cards: null,
  product_cards_error: null,
  product_cards_start: false,

  // Products which have formula
  products_having_formula: null,
  product_search_str: null,
  fetch_products_having_formula_error: null,
  fetch_products_having_formula_start: false,

  frms: [],
  fetch_frms_error: null,
  fetch_frms_start: false,
  alternative_matching: false,

  // Local formula related
  local_frms: [],
  local_fetch_frms_error: null,
  local_fetch_frms_start: false,

  compatible_local_frms: [],
  compatible_local_fetch_frms_error: null,
  compatible_local_fetch_frms_start: false,

  custom_formula_mode: CUSTOM_FORMULA_ORIGINAL,

  // Matching results
  search_closest_rs: {},
  search_closest_rs_error: null,
  search_closest_rs_start: false,
  match_rs: null,
  match_rs_error: null,
  match_rs_start: false,
  correct_rs: {},
  correct_rs_error: null,
  correct_rs_start: false,

  // color details
  color_details: null,
  color_details_error: null,
  color_details_start: false,
  // primer details
  recommended_primer: null,
  recommended_primer_error: null,
  recommended_primer_start: false,
  recommended_primer_ack: false,

  // formula changes
  formula_changes: null,
  fetch_formula_changes_error: null,
  fetch_formula_changes_start: false,
};

function payloadAs<Payload>(action: FormulaAction): Payload {
  return action.payload as Payload;
}

function reducer(
  state: FormulaState = initial_state,
  action: FormulaAction
): FormulaState {
  switch (action.type) {
    case types.CLEAR_SEARCHES: {
      return {
        ...initial_state,
        closest_search_colorcard: state.closest_search_colorcard,
      };
    }
    case types.FETCH_PRODUCT_CARDS_PENDING: {
      return { ...state, product_cards_start: true, product_cards_error: null };
    }
    case types.FETCH_PRODUCT_CARDS_FULFILLED: {
      return {
        ...state,
        product_cards_start: false,
        product_cards: payloadAs<number[] | null>(action),
        product_cards_error: null,
      };
    }
    case types.FETCH_PRODUCT_CARDS_REJECTED: {
      return {
        ...state,
        product_cards_start: false,
        product_cards: [],
        product_cards_error: action.payload,
      };
    }
    case types.FETCH_FORMULA_PENDING: {
      return { ...state, fetch_frms_start: true, fetch_frms_error: null };
    }
    case types.FETCH_FORMULA_REJECTED: {
      return {
        ...state,
        fetch_frms_start: false,
        frms: [],
        fetch_frms_error: action.payload,
      };
    }
    case types.FETCH_FORMULA_FULFILLED: {
      return {
        ...state,
        fetch_frms_start: false,
        frms: payloadAs<FormulaVersion[]>(action),
        fetch_frms_error: null,
      };
    }
    case types.FETCH_LOCAL_FORMULA_PENDING: {
      return {
        ...state,
        local_fetch_frms_start: true,
        local_fetch_frms_error: null,
      };
    }
    case types.FETCH_LOCAL_FORMULA_REJECTED: {
      return {
        ...state,
        local_fetch_frms_start: false,
        local_frms: [],
        local_fetch_frms_error: action.payload,
      };
    }
    case types.FETCH_LOCAL_FORMULA_FULFILLED: {
      return {
        ...state,
        local_fetch_frms_start: false,
        local_frms: payloadAs<LocalFormula[]>(action),
        local_fetch_frms_error: null,
      };
    }
    case types.HIDE_LOCAL_FORMULA: {
      const itemid = payloadAs<number>(action);
      return {
        ...state,
        local_frms: state.local_frms.filter((x) => x.itemid !== itemid),
      };
    }
    case types.FETCH_COMPATIBLE_LOCALFRM_PROD_PENDING: {
      return {
        ...state,
        compatible_local_fetch_frms_start: true,
        compatible_local_fetch_frms_error: null,
      };
    }
    case types.FETCH_COMPATIBLE_LOCALFRM_PROD_REJECTED: {
      return {
        ...state,
        compatible_local_fetch_frms_start: false,
        compatible_local_frms: [],
        compatible_local_fetch_frms_error: action.payload,
      };
    }
    case types.FETCH_COMPATIBLE_LOCALFRM_PROD_FULFILLED: {
      return {
        ...state,
        compatible_local_fetch_frms_start: false,
        compatible_local_frms: payloadAs<CompatibleLocalFormulaState[]>(action),
        compatible_local_fetch_frms_error: null,
      };
    }
    case types.SET_CUSTOM_FORMULA_MODE: {
      return {
        ...state,
        custom_formula_mode: payloadAs<CustomFormulaMode>(action),
      };
    }
    case types.FETCH_PROD_WITH_FORMULA_PENDING: {
      return {
        ...state,
        products_having_formula: [],
        fetch_products_having_formula_start: true,
        fetch_products_having_formula_error: null,
      };
    }
    case types.FETCH_PROD_WITH_FORMULA_REJECTED: {
      return {
        ...state,
        fetch_products_having_formula_start: false,
        fetch_products_having_formula_error: action.payload,
      };
    }
    case types.FETCH_PROD_WITH_FORMULA_FULFILLED: {
      return {
        ...state,
        fetch_products_having_formula_start: false,
        products_having_formula: payloadAs<
          ProductWithFormula[] | Record<string, never> | null
        >(action),
        fetch_products_having_formula_error: null,
      };
    }

    case types.FETCH_COLORS_PENDING: {
      return { ...state, fetch_colors_start: true, fetch_colors_error: null };
    }
    case types.FETCH_COLORS_REJECTED: {
      return {
        ...state,
        fetch_colors_start: false,
        colors: [],
        fetch_colors_error: action.payload,
      };
    }
    case types.FETCH_COLORS_FULFILLED: {
      const payload = payloadAs<ColorSearchResponsePayload>(action);
      return {
        ...state,
        fetch_colors_start: false,
        colors: payload.data,
        fetch_colors_error: null,
        color_search_str: payload.search,
      };
    }

    case types.SEARCH_CLOSEST_PENDING: {
      return {
        ...state,
        search_closest_rs_start: true,
        search_closest_rs_error: null,
      };
    }
    case types.SEARCH_CLOSEST_REJECTED: {
      return {
        ...state,
        search_closest_rs_start: false,
        search_closest_rs: {},
        search_closest_rs_error: action.payload,
      };
    }
    case types.SEARCH_CLOSEST_FULFILLED: {
      return {
        ...state,
        search_closest_rs_start: false,
        search_closest_rs: payloadAs<MatchingResponse>(action),
        search_closest_rs_error: null,
      };
    }

    case types.MATCH_FORMULA_PENDING: {
      return { ...state, match_rs_start: true, match_rs_error: null };
    }
    case types.MATCH_FORMULA_REJECTED: {
      return {
        ...state,
        match_rs_start: false,
        match_rs: null,
        match_rs_error: action.payload,
      };
    }
    case types.MATCH_FORMULA_FULFILLED: {
      return {
        ...state,
        match_rs_start: false,
        match_rs: payloadAs<MatchingResponse>(action),
        match_rs_error: null,
      };
    }

    case types.CORRECT_FORMULA_PENDING: {
      return { ...state, correct_rs_start: true, correct_rs_error: null };
    }
    case types.CORRECT_FORMULA_REJECTED: {
      return {
        ...state,
        correct_rs_start: false,
        correct_rs: {},
        correct_rs_error: action.payload,
      };
    }
    case types.CORRECT_FORMULA_FULFILLED: {
      return {
        ...state,
        correct_rs_start: false,
        correct_rs: payloadAs<FormulaCorrectionResult>(action),
        correct_rs_error: null,
      };
    }

    case types.FETCH_COLOR_DETAILS_PENDING: {
      return { ...state, color_details_start: true, color_details_error: null };
    }
    case types.FETCH_COLOR_DETAILS_REJECTED: {
      return {
        ...state,
        color_details_start: false,
        color_details: null,
        match_rs_error: action.payload,
      };
    }
    case types.FETCH_COLOR_DETAILS_FULFILLED: {
      return {
        ...state,
        color_details_start: false,
        color_details: payloadAs<FormulaColorDetails>(action),
        color_details_error: null,
      };
    }
    case types.CHOOSE_PRIMER_PENDING: {
      return {
        ...state,
        recommended_primer_start: true,
        recommended_primer_error: null,
        recommended_primer: null,
        recommended_primer_ack: false,
      };
    }
    case types.CHOOSE_PRIMER_REJECTED: {
      return {
        ...state,
        recommended_primer_start: false,
        recommended_primer: null,
        recommended_primer_error: action.payload,
      };
    }
    case types.CHOOSE_PRIMER_FULFILLED: {
      return {
        ...state,
        recommended_primer_start: false,
        recommended_primer: payloadAs<PrimerColourState | null>(action),
        recommended_primer_error: null,
      };
    }

    case types.CLEAR_PRIMER: {
      return {
        ...state,
        recommended_primer_start: false,
        recommended_primer: null,
        recommended_primer_error: null,
        recommended_primer_ack: false,
      };
    }

    case types.ACK_PRIMER: {
      return {
        ...state,
        recommended_primer_ack: true,
      };
    }

    case types.SET_COLOR_DETAILS: {
      return {
        ...state,
        color_details: payloadAs<FormulaColorDetails>(action),
      };
    }
    case types.SET_CLOSEST_SEARCH_COLORCARD: {
      return {
        ...state,
        closest_search_colorcard: payloadAs<ClosestSearchColorCard>(action),
      };
    }
    case types.ALTERNATIVE_MATCHING: {
      return {
        ...state,
        alternative_matching: payloadAs<boolean>(action),
      };
    }

    case types.FETCH_FORMULA_CHANGES_PENDING: {
      return {
        ...state,
        fetch_formula_changes_start: true,
        fetch_formula_changes_error: null,
      };
    }
    case types.FETCH_FORMULA_CHANGES_REJECTED: {
      return {
        ...state,
        fetch_formula_changes_start: false,
        formula_changes: null,
        fetch_formula_changes_error: action.payload,
      };
    }
    case types.FETCH_FORMULA_CHANGES_FULFILLED: {
      return {
        ...state,
        fetch_formula_changes_start: false,
        formula_changes: payloadAs<FormulaChange[]>(action),
        fetch_formula_changes_error: null,
      };
    }

    default: {
      return state;
    }
  }
}

const mountPath = 'formula';

const localSelectors = {
  recommended_primer: (state: FormulaState): PrimerColour | null =>
    state.recommended_primer,
  primer_check_pending: (state: FormulaState): boolean =>
    state.recommended_primer_start,
  show_primer: (state: FormulaState): boolean | null =>
    (state.recommended_primer_start || state.recommended_primer) &&
    !state.recommended_primer_ack,
  color_search_str: (state: FormulaState): Nullable<string> =>
    state.color_search_str,
  product_search_str: (state: FormulaState): Nullable<string> =>
    state.product_search_str,
  products_having_formula: (
    state: FormulaState
  ): FormulaState['products_having_formula'] => state.products_having_formula,
  compatible_local_frms: (state: FormulaState): CompatibleLocalFormulaState[] =>
    state.compatible_local_frms,
  local_frms: (state: FormulaState): LocalFormula[] => state.local_frms,
  custom_formula_mode: (state: FormulaState): CustomFormulaMode =>
    state.custom_formula_mode,
  get_formula_correction: (state: FormulaState, key: string): unknown =>
    (state.correct_rs ?? {})[key as keyof NonNullable<typeof state.correct_rs>],
  alternative_matching: (state: FormulaState): boolean =>
    state.alternative_matching,
  frms: (state: FormulaState): FormulaVersion[] => state.frms,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
